import { useEffect, useMemo, useState } from 'react';
import { getPlayerBookings } from '@digiwedge/teetime-api-client';
const mapBookingStatus = (status) => {
    const normalized = (status ?? '').toUpperCase();
    if (normalized === 'PAID')
        return 'PAID';
    if (normalized === 'CONFIRMED')
        return 'PAID';
    if (normalized === 'CANCELLED')
        return 'FAILED';
    if (normalized === 'PENDING')
        return 'PENDING';
    return 'UNPROCESSED';
};
const mapBookingToTransaction = (booking) => ({
    id: String(booking.BookingId),
    invoice: booking.BookingReference ?? String(booking.BookingId),
    amount: booking.TotalPrice ?? undefined,
    paymentDate: booking.ReservationDate ?? booking.BookingDate ?? undefined,
    paidOn: booking.PaymentDate ?? undefined,
    type: 'BOOKING',
    status: mapBookingStatus(booking.Status),
    createdAt: booking.BookingDate ?? undefined,
    organisation: {
        displayName: booking.ClubName ?? undefined,
        name: booking.CourseName ?? undefined,
    },
    // Extend with booking-specific metadata
    receipt: booking.PaymentReference ?? undefined,
    // Store extra booking info in category for downstream use
    category: booking.Status ?? undefined,
});
const mapBookingToEnhanced = (booking) => {
    const base = mapBookingToTransaction(booking);
    const teeTime = booking.ReservationDate;
    const now = new Date();
    const reservationDate = teeTime ? new Date(teeTime) : null;
    const isUpcoming = reservationDate ? reservationDate > now : false;
    const isCancelled = (booking.Status ?? '').toUpperCase() === 'CANCELLED';
    return {
        ...base,
        bookingId: String(booking.BookingId),
        clubName: booking.ClubName ?? undefined,
        courseName: booking.CourseName ?? undefined,
        teeTime: teeTime ?? undefined,
        players: booking.NumberPlayers ?? undefined,
        currencySymbol: booking.CurrencySymbol ?? 'R',
        isUpcoming,
        isCancelled,
    };
};
export const usePlayerTransactions = (email, options = {}) => {
    const lookbackDays = options.lookbackDays ?? 90;
    const showUpcoming = options.showUpcoming ?? true;
    const [{ startDate }] = useState(() => {
        const now = new Date();
        const start = new Date(now.getTime() - lookbackDays * 24 * 60 * 60 * 1000).toISOString();
        return { startDate: start };
    });
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        let cancelled = false;
        if (!email) {
            setTransactions([]);
            setLoading(false);
            setError(null);
            return () => {
                cancelled = true;
            };
        }
        setLoading(true);
        setError(null);
        void getPlayerBookings(email)
            .then((res) => {
            if (cancelled)
                return;
            const playerBookings = Array.isArray(res.PlayerBookings) ? res.PlayerBookings : [];
            // Sort by reservation date, upcoming first, then by date descending
            const sorted = [...playerBookings].sort((a, b) => {
                const dateA = a.ReservationDate ? new Date(a.ReservationDate).getTime() : 0;
                const dateB = b.ReservationDate ? new Date(b.ReservationDate).getTime() : 0;
                const now = Date.now();
                const aUpcoming = dateA > now;
                const bUpcoming = dateB > now;
                // Upcoming bookings first
                if (aUpcoming && !bUpcoming)
                    return -1;
                if (!aUpcoming && bUpcoming)
                    return 1;
                // Within same category, sort by date (upcoming: ascending, past: descending)
                if (aUpcoming)
                    return dateA - dateB;
                return dateB - dateA;
            });
            // Filter to include upcoming and recent past (within lookbackDays)
            const filtered = sorted.filter((booking) => {
                const reservationDate = booking.ReservationDate
                    ? new Date(booking.ReservationDate).toISOString()
                    : null;
                if (!reservationDate)
                    return true; // Include if no date
                const isUpcoming = new Date(reservationDate) > new Date();
                if (showUpcoming && isUpcoming)
                    return true;
                return reservationDate >= startDate;
            });
            const mapped = filtered.map(mapBookingToEnhanced);
            setTransactions(mapped);
        })
            .catch((err) => {
            if (cancelled)
                return;
            setError(err instanceof Error ? err : new Error(String(err)));
            setTransactions([]);
        })
            .finally(() => {
            if (cancelled)
                return;
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [email, showUpcoming, startDate]);
    const formatDate = useMemo(() => (iso) => {
        const date = new Date(iso);
        if (Number.isNaN(date.getTime()))
            return iso;
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    }, []);
    const formatTime = useMemo(() => (iso) => {
        const date = new Date(iso);
        if (Number.isNaN(date.getTime()))
            return '';
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
        });
    }, []);
    const upcomingCount = useMemo(() => transactions.filter((t) => t.isUpcoming && !t.isCancelled).length, [transactions]);
    return {
        transactions,
        loading,
        error,
        startDate,
        formatDate,
        formatTime,
        upcomingCount,
    };
};
//# sourceMappingURL=usePlayerTransactions.js.map